import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { CategoryAttributesWriteRequest } from '@/api/handlers/resources/categories/requests';
import type { GetCategoryAttributesResponse } from '@/api/handlers/resources/categories/responses';
import type { ApiResponse } from '@/api/handlers/responses';
import { CategoryQueryKeys } from './categoryQueryKeys';

export type PutCategoryAttributesVariables = {
    categoryId: string;
    body: CategoryAttributesWriteRequest;
};

/**
 * Replace a category's whole attribute set in one round-trip
 * (`PUT /categories/{id}/attributes`). On success invalidates that category's
 * attributes query so the drawer re-reads the persisted, server-ordered list.
 */
export const usePutCategoryAttributesMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<
        ApiResponse<GetCategoryAttributesResponse>,
        Error,
        PutCategoryAttributesVariables
    >({
        mutationFn: ({ categoryId, body }) =>
            apiClient.categories.putCategoryAttributes(categoryId, body),
        onSuccess: (result, { categoryId }) => {
            if (result.success) {
                queryClient.invalidateQueries({
                    queryKey: CategoryQueryKeys.attributes(categoryId),
                });
            }
        },
    });
};
